import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Check, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Eye, Gift, Lock, Package, X } from 'lucide-react';
import api from '../../../services/api';
import useQuizStore from '../../../store/quizStore';
import useAuthStore from '../../../store/authStore';
import ProgressBar from '../../../components/ui/ProgressBar';
import ThemeToggle from '../../../components/ui/ThemeToggle';
import Quiz from '../Quiz';
import { scoreColor } from './resultats/helpers';
import NotionCheckoutModal, { fmtEuro } from '../../../components/stripe/NotionCheckoutModal';
import RichContent from '../../../components/content/RichContent';
// Illustrations d'en-tête : notions-illustration-<slug de la thématique>.webp si elle existe, sinon l'image par défaut.
const NOTIONS_ART = import.meta.glob('../../../assets/notions-illustration*.webp', { eager: true, import: 'default' });
const notionsArtFor = (slug) => NOTIONS_ART[`../../../assets/notions-illustration-${slug}.webp`] || NOTIONS_ART['../../../assets/notions-illustration.webp'];

/**
 * Onglet « Notions » d'une thématique (migrations 183-186). Une notion = une fiche de révision découpée en
 * parties + 20 QCM construits sur la fiche ; un achat donne un accès illimité. Elle se présente comme un sujet (même carte, même
 * fenêtre que QcmCard) ; la fiche se lit partie par partie dans l'habillage du quiz (fond, bandeau de la
 * thématique, pastilles, précédente / suivante). Le test s'ouvre une fois la fiche lue en entier.
 * Ses séances tournent dans la thématique technique « notions » (hors compteurs de la thématique).
 * Données : notions/list.php, notions/detail.php (parties, droits, séances), notions/read.php (partie lue).
 * Achat : une notion à son prix, ou un pack de N notions à prix fixe (pack_offers type « notion ») dont les notions
 * se choisissent ensuite une à une, comme la notion offerte avec l'abonnement (notions/claim.php).
 */
const readingMinutes = (mots) => Math.max(1, Math.round((mots || 0) / 200));
const fmtPrix = (p) => (Number.isInteger(p) ? String(p) : p.toFixed(2).replace('.', ',')) + ' €';
const fmtDate = (d) => d ? new Date(String(d).replace(' ', 'T')).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
const pctOf = (s) => (s.total ? Math.round(s.score / s.total * 100) : 0);
const bestColor = (p) => (p >= 75 ? '#28a745' : p >= 50 ? '#e67e22' : '#dc3545');
/** Achetable : payante, en ligne, ni acquise ni jouable (l'admin joue tout, il n'achète pas). */
export const isBuyable = (n) => !n.is_free && !n.owned && !n.playable && n.status === 'ONLINE' && n.prix > 0;

// Droits et achats partagés par les cartes, la fenêtre et le lecteur de l'onglet.
const BuyCtx = createContext(null);

export default function TabNotions({ scopeInfo, scopeSlug, notions, previewMode, offerCredits = 0, packCredits = 0, packs = [], onRefresh, focusHead = false }) {
    const [popupSlug, setPopupSlug] = useState(null);
    // Arrivée par un lien direct (?onglet=notions, ex. mail d'annonce) ou par l'encart « notions à choisir » :
    // on amène l'en-tête « Notions » à l'écran, une seule fois, au lieu de laisser la personne chercher sous le menu des modes.
    const headRef = useRef(null);
    useEffect(() => {
        if (!focusHead && new URLSearchParams(window.location.search).get('onglet') !== 'notions') return undefined;
        const t = setTimeout(() => headRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
        return () => clearTimeout(t);
    }, []);
    const [checkout, setCheckout] = useState(null); // { kind: 'notion', … } ou { kind: 'pack', offer }
    const [banner, setBanner] = useState(() => {
        const q = new URLSearchParams(window.location.search);
        return q.get('notions_pack') === 'ok' ? 'pack' : q.get('notions_achat') === 'ok' ? 'notion' : null;
    });

    const buyable = notions.filter(isBuyable);
    // Les notions n'ont pas toutes le même prix : la remise d'un pack dépend des notions choisies. On n'affiche que
    // l'économie GARANTIE, calculée sur les notions les moins chères (au moins X %), et la fourchette des prix.
    const prices = buyable.map(n => n.prix);
    const unitMin = prices.length ? Math.min(...prices) : 0;
    const unitMax = prices.length ? Math.max(...prices) : 0;
    const credits = offerCredits + packCredits;
    const ctx = {
        credits,
        packs, unitMin, unitMax,
        buyNow: (n) => setCheckout({ kind: 'notion', id: n.id, titre: n.titre, prix: n.prix }),
        buyPack: (offer) => setCheckout({ kind: 'pack', offer, unitMin, unitMax }),
        claim: (n) => api.post('/notions/claim.php', { notion_id: n.id, scope_id: scopeInfo?.id }).then(() => onRefresh?.()),
    };

    // Retour de Stripe : le webhook enregistre l'achat en quelques secondes ; on recharge la liste.
    useEffect(() => {
        if (!banner) return undefined;
        try {
            const u = new URL(window.location.href);
            ['notions_achat', 'notions_pack', 'session_id'].forEach(k => u.searchParams.delete(k));
            window.history.replaceState(null, '', u.pathname + u.search);
        } catch { /* sans effet */ }
        const timers = [1500, 4000, 8000, 15000].map(ms => setTimeout(() => onRefresh?.(), ms));
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [banner]);

    return (
        <BuyCtx.Provider value={ctx}>
        <div className="notions" style={scopeInfo?.couleur_theme ? { '--notion-accent': scopeInfo.couleur_theme } : undefined}>
            <div className="notions__head notions__head--lib" ref={headRef}>
                <div className="notions__head-text">
                    <span className="notions__kicker">Fiches de révision</span>
                    <h2 className="notions__title">Notions</h2>
                    <p>Une fiche pour comprendre l'essentiel d'un sujet, puis 20 questions pour vérifier ce que tu as retenu.</p>
                    <p className="notions__buy-note">Paiement unique, accès illimité aux notions achetées.</p>
                    {previewMode && <span className="notions__preview"><Eye size={13} /> Aperçu avant ouverture : visible seulement par les comptes autorisés</span>}
                </div>
                <NotionsArt slug={scopeSlug} />
            </div>
            {banner && (
                <div className="notion-paid-banner">
                    <CheckCircle size={18} />
                    <span><strong>Paiement reçu, merci !</strong> {banner === 'pack'
                        ? 'Ton pack est activé dans quelques secondes : ouvre ensuite les notions de ton choix et touche « Choisir cette notion ».'
                        : 'Ta notion se débloque dans quelques secondes.'} La facture t'est envoyée par e-mail.</span>
                    <button type="button" onClick={() => setBanner(null)} aria-label="Fermer"><X size={16} /></button>
                </div>
            )}
            {credits > 0 && buyable.length > 0 && (
                <div className="notion-bundle notion-bundle--gift">
                    <Gift size={18} />
                    <span><strong>Il te reste {credits > 1 ? `${credits} notions` : 'une notion'} à choisir</strong>
                        {offerCredits > 0 && packCredits > 0 ? ' (abonnement et pack).' : offerCredits > 0 ? ', offerte avec ton abonnement.' : ', avec ton pack.'}
                        {' '}Ouvre la notion de ton choix et touche « Choisir cette notion ».</span>
                </div>
            )}
            {credits === 0 && packs.length > 0 && buyable.length > 1 && (
                <p className="nlib-shelf-note">
                    <Package size={15} /> Plusieurs notions à réviser ?{' '}
                    {packs.map((p, i) => (
                        <span key={p.id}>{i > 0 && ' · '}<button type="button" className="nlib-link" onClick={() => ctx.buyPack(p)}>{p.notions} notions au choix pour {fmtEuro(p.price)}</button></span>
                    ))}, à choisir quand tu veux.
                </p>
            )}
            {notions.length === 0 && <p className="notions__empty">Pas encore de notion pour ce parcours : choisis « Tout » pour voir les autres.</p>}
            <div className="sl-qcm-grid notions__grid">
                {notions.map(n => <NotionCard key={n.id} n={n} onOpen={() => setPopupSlug(n.slug)} />)}
            </div>
            {popupSlug && createPortal(
                <NotionPopup slug={popupSlug} scopeInfo={scopeInfo} onClose={() => { setPopupSlug(null); onRefresh?.(); }} />,
                document.body
            )}
            {checkout && createPortal(
                <NotionCheckoutModal item={checkout} scopeSlug={scopeSlug}
                    onClose={() => setCheckout(null)} onSimulated={() => { setPopupSlug(null); onRefresh?.(); }} />,
                document.body
            )}
        </div>
        </BuyCtx.Provider>
    );
}

/** Illustration d'en-tête (fiches et livre ouvert, image détourée), propre à la thématique quand elle existe. */
function NotionsArt({ slug }) {
    const src = notionsArtFor(slug);
    return src ? <img className="notions__art" src={src} alt="" width="240" height="138" loading="lazy" /> : null;
}

function NotionBadges({ n }) {
    return (
        <>
            {n.status && n.status !== 'ONLINE' && <span className="qcm-badge-edition">Brouillon</span>}
            {n.is_free
                ? <span className="qcm-badge-demo">Gratuit</span>
                : n.owned ? <span className="notion-badge notion-badge--owned"><Check size={10} /> Acquise</span>
                    : <span className="notion-badge notion-badge--price">{fmtPrix(n.prix)}</span>}
        </>
    );
}

/** Carte d'une notion : même balisage que QcmCard (carte de sujet). */
function NotionCard({ n, onOpen }) {
    const total = n.sections_total || 0;
    const lues = Math.min(n.sections_read || 0, total);
    const lusPct = total ? Math.round(lues / total * 100) : 0;
    const ficheLue = total > 0 && lues >= total;
    const [descExpanded, setDescExpanded] = useState(false);
    // Résumé coupé à 3 lignes sur toutes les cartes (hauteurs régulières) ; « Lire la suite » au-delà d'une centaine de caractères.
    const descTruncatable = !!(n.accroche && n.accroche.length > 110);
    const cta = !n.playable ? 'Aperçu gratuit' : ficheLue ? 'Se tester' : lues > 0 ? 'Reprendre la fiche' : 'Lire la fiche';

    return (
        <div className={`qcm-card qcm-card-clickable notion-card notion-card--lib${n.status !== 'ONLINE' ? ' qcm-card-edition' : ''}`} onClick={onOpen}>
            <div className="qcm-card-header">
                <div className="qcm-card-title-row">
                    <h4 className="qcm-card-title">{n.titre}</h4>
                    <NotionBadges n={n} />
                </div>
                {n.accroche && (
                    <>
                        <p className={`qcm-card-desc${descTruncatable && !descExpanded ? ' qcm-card-desc--clamped' : ''}`}>{n.accroche}</p>
                        {descTruncatable && (
                            <button type="button" className="qcm-card-desc-toggle" onClick={(e) => { e.stopPropagation(); setDescExpanded(v => !v); }}>
                                {descExpanded ? 'Réduire' : 'Lire la suite'}
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="qcm-card-meta">
                <span className="qcm-card-questions">Fiche ≈ {readingMinutes(n.fiche_mots)} min · {n.nb_questions} questions</span>
            </div>

            {lues > 0 && (
                <div className="qcm-card-progression-dual">
                    <div className="qcm-prog-row" title={`${lues} parties lues sur ${total}`}>
                        <BookOpen size={12} className="qcm-prog-icon qcm-prog-icon-seen" />
                        <span className="qcm-prog-label">Fiche lue</span>
                        <div className="qcm-progression-bar"><div className="qcm-progression-fill qcm-progression-fill-seen" style={{ width: `${lusPct}%` }} /></div>
                        <span className="qcm-progression-label">{lues}/{total}</span>
                    </div>
                </div>
            )}

            {n.best_pct != null && (
                <div className="qcm-card-stats">
                    <div className="qcm-stat">
                        <div className="qcm-stat-bar"><div className="qcm-stat-fill" style={{ width: `${n.best_pct}%`, background: bestColor(n.best_pct) }} /></div>
                        <span className="qcm-stat-label">Meilleur : {n.best_pct}%</span>
                    </div>
                    {n.sessions > 0 && <span className="qcm-stat-attempts">{n.sessions} tentative{n.sessions > 1 ? 's' : ''}</span>}
                </div>
            )}

            <div className="qcm-card-action notion-card__actions" onClick={e => e.stopPropagation()}>
                <button type="button" onClick={onOpen} className={`btn ${n.playable ? 'btn-primary' : 'btn-outline'} btn-sm qcm-card-btn`}>{cta}</button>
            </div>
        </div>
    );
}

/** Fenêtre de la notion : même habillage que la fenêtre d'un sujet (CardDetailPopup). */
function NotionPopup({ slug, scopeInfo, onClose }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);
    const [reader, setReader] = useState(null); // partie ouverte dans le lecteur
    const [quizMode, setQuizMode] = useState(null);
    const [readerOver, setReaderOver] = useState(null); // partie ouverte par-dessus le quiz (« Revoir dans la fiche »)
    const [pickerMode, setPickerMode] = useState('apprentissage');

    const load = useCallback(() => {
        api.get('/notions/detail.php', { params: { slug, scope_id: scopeInfo?.id } })
            .then(({ data }) => setD(data.data))
            .catch(e => setErr(e.response?.data?.message || 'Notion indisponible'));
    }, [slug, scopeInfo?.id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const overlayBg = d?.matiere_image ? { backgroundImage: `url(${d.matiere_image})` } : {};

    if (reader !== null && d) {
        return <NotionReader d={d} start={reader} scopeInfo={scopeInfo} overlayBg={overlayBg}
            onRead={(read_sections, read) => setD(x => ({ ...x, read_sections, read: read || x.read }))}
            onClose={() => setReader(null)} onClaimed={load} />;
    }
    if (quizMode && d) {
        // Renvoi vers la ou les parties de la fiche où la réponse se vérifie (migration 187).
        const revoir = (q) => <NotionRevoir d={d} question={q} onOpen={setReaderOver} />;
        return (
            <>
                <div className={`quiz-modal-overlay ${overlayBg.backgroundImage ? 'has-bg-image' : ''}`} style={overlayBg}>
                    <Quiz sousSujetIdProp={d.quiz.sous_sujet_id} scopeIdProp={d.quiz.scope_id}
                        scopeInfoProp={scopeInfo}
                        nbQuestionsProp={d.nb_questions || 20} modeProp={quizMode} originProp="subject"
                        afterExplanation={revoir}
                        onClose={() => { useQuizStore.getState().reset(); setQuizMode(null); setReaderOver(null); load(); }} />
                </div>
                {readerOver !== null && (
                    <NotionReader d={d} start={readerOver} scopeInfo={scopeInfo} overlayBg={overlayBg} onTop
                        onRead={(read_sections, read) => setD(x => ({ ...x, read_sections, read: read || x.read }))}
                        onClose={() => setReaderOver(null)} onClaimed={load} />
                )}
            </>
        );
    }

    const total = d?.sections?.length || 0;
    const readSet = new Set(d?.read_sections || []);
    const lues = Math.min(readSet.size, total);
    const ficheLue = !!d?.read || (total > 0 && lues >= total);
    const nextUnread = d ? (d.sections.find(s => !readSet.has(s.idx) && !s.locked)?.idx ?? 0) : 0;
    const best = d && d.sessions.length ? Math.max(...d.sessions.map(pctOf)) : null;
    const lusPct = total ? Math.round(lues / total * 100) : 0;

    const accent = scopeInfo?.couleur_theme || null;
    const readLabel = lues === 0 ? (d?.locked ? 'Feuilleter la fiche' : 'Lire la fiche') : ficheLue ? 'Relire la fiche'
        : `Reprendre à ${nextUnread === 0 ? "L'essentiel" : `la partie ${nextUnread}`}`;

    return (
        <div className="qcm-popup-overlay" onClick={onClose}>
            <div className="qcm-popup notion-popup notion-popup--lib" onClick={(e) => e.stopPropagation()}
                style={accent ? { '--notion-accent': accent } : undefined}>
                <button className="qcm-popup-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                {err ? <p className="alert alert-error">{err}</p> : !d ? <div className="loading">Chargement…</div> : (
                    <>
                        <div className="nlib-head">
                            <NotionCover d={d} />
                            <div className="nlib-info">
                                <span className="nlib-kicker"><span className="nlib-kicker__kind">Fiche de révision{d.matiere ? ' · ' : ''}</span>{d.matiere || ''}
                                    {d.status && d.status !== 'ONLINE' && <span className="qcm-badge-edition">Brouillon</span>}</span>
                                <h3 className="nlib-title">{d.titre}</h3>
                                {d.accroche && <p className="nlib-desc">{d.accroche}</p>}
                                <p className="nlib-meta">
                                    {total} parties · {readingMinutes(d.fiche_mots)} min de lecture · {d.nb_questions} questions
                                    {d.is_free ? <span className="nlib-free">Gratuite</span> : d.owned ? <span className="nlib-owned"><Check size={12} /> Dans ta bibliothèque</span> : null}
                                </p>
                                {lues > 0 && (
                                    <div className="nlib-progress" title={`${lues} parties lues sur ${total}`}>
                                        <div className="nlib-progress__bar"><div style={{ width: `${lusPct}%` }} /></div>
                                        <span>{ficheLue ? 'Fiche lue en entier' : `${lues} partie${lues > 1 ? 's' : ''} lue${lues > 1 ? 's' : ''} sur ${total}`}</span>
                                    </div>
                                )}
                                <button type="button" className="nlib-read" onClick={() => setReader(ficheLue ? 0 : nextUnread)}>
                                    {readLabel}{d.locked && lues > 0 ? ' (aperçu)' : ''} →
                                </button>
                            </div>
                        </div>

                        <div className="nlib-foot">
                            {!d.playable ? <Unlock d={d} onClaimed={load} /> : !ficheLue ? (
                                <div className="nlib-gate">
                                    <Lock size={15} />
                                    <span><strong>Le test ({d.nb_questions} questions)</strong> s'ouvre quand toute la fiche est lue ({lues}/{total} parties) : chaque question se vérifie dans la fiche.</span>
                                </div>
                            ) : (
                                <div className="qcm-popup-launch">
                                    <span className="nlib-foot__label">Le test · {d.nb_questions} questions</span>
                                    <div className="mode-picker-section">
                                        <div className="mode-picker-options mode-picker-options--row">
                                            <button className={`mode-option mode-option-apprentissage ${pickerMode === 'apprentissage' ? 'selected' : ''}`} onClick={() => setPickerMode('apprentissage')}>
                                                <span className="mode-option-icon"><BookOpen size={20} strokeWidth={1.8} /></span>
                                                <strong>Apprentissage</strong>
                                                <span className="mode-option-desc">Correction immédiate</span>
                                            </button>
                                            <button className={`mode-option mode-option-quiz ${pickerMode === 'quiz' ? 'selected' : ''}`} onClick={() => setPickerMode('quiz')}>
                                                <span className="mode-option-icon"><Clock size={20} strokeWidth={1.8} /></span>
                                                <strong>Classique</strong>
                                                <span className="mode-option-desc">Résultat à la fin</span>
                                            </button>
                                        </div>
                                    </div>
                                    <button className="btn nlib-btn" style={{ width: '100%', marginTop: '0.5rem' }} onClick={() => setQuizMode(pickerMode)}>
                                        Lancer le test
                                    </button>
                                    {best != null && <span className="nlib-best">Meilleur score : <strong style={{ color: scoreColor(best) }}>{best} %</strong> · {d.sessions.length} tentative{d.sessions.length > 1 ? 's' : ''}</span>}
                                </div>
                            )}
                        </div>

                        {d.sessions.length > 0 && (
                            <div className="notion-popup__history">
                                <span className="mode-picker-label">Derniers résultats</span>
                                {d.sessions.slice(0, 3).map(s => (
                                    <div key={s.id} className="notion-popup__session">
                                        <span>{fmtDate(s.created_at)} · {s.mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}</span>
                                        <strong style={{ color: scoreColor(pctOf(s)) }}>{s.score}/{s.total}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/** Titre de couverture : titre_court s'il existe, sinon la partie après « : » ou « , » (« De Rome à Lisbonne »). */
const coverTitle = (d) => {
    if (d.titre_court) return d.titre_court;
    const t = String(d.titre || '').replace(/\s*\(session [^)]*\)\s*$/i, '');
    const m = t.match(/^[^:,]{6,}[:,]\s*(.{6,})$/);
    const c = m ? m[1] : t;
    return c.charAt(0).toUpperCase() + c.slice(1);
};

/** Couverture de « livre » d'une notion : texte et couleur de la thématique, aucune image à produire. */
function NotionCover({ d, small = false }) {
    const title = coverTitle(d);
    return (
        <div className={`nlib-cover${small ? ' nlib-cover--small' : ''}${title.length > 48 ? ' nlib-cover--xlong' : title.length > 30 ? ' nlib-cover--long' : ''}`} aria-hidden="true">
            <span className="nlib-cover__kicker">{d.matiere || 'Notion'}</span>
            <span className="nlib-cover__title">{title}</span>
            <span className="nlib-cover__foot">MonQuiz<br />Fiche de révision</span>
        </div>
    );
}

/** Lecteur de fiche partie par partie, dans l'habillage du quiz (fond, bandeau, pastilles, navigation). */
/**
 * « Revoir dans la fiche » sous l'explication d'une question : la ou les parties de la fiche où la réponse se vérifie
 * (d.question_sections, migration 187). Rien si la question n'est rattachée à aucune partie.
 * Exporté : sert aussi à la relecture d'une séance depuis l'onglet Résultats (NotionHistoryView).
 */
export function NotionRevoir({ d, question, onOpen }) {
    const idxs = (d?.question_sections || {})[question?.id] || [];
    if (!idxs.length) return null;
    return (
        <div className="notion-revoir">
            <BookOpen size={15} /> <span>Revoir dans la fiche :</span>
            {idxs.map(i => {
                const sec = d.sections.find(x => x.idx === i);
                return <button key={i} type="button" className="notion-revoir__btn" onClick={() => onOpen(i)}>
                    {i === 0 ? "L'essentiel" : `Partie ${i}`}{sec ? ` — ${sec.titre.replace(/^\d+\.\s*/, '')}` : ''}</button>;
            })}
        </div>
    );
}

export function NotionReader({ d, start, scopeInfo, overlayBg, onRead, onClose, onClaimed, onTop = false }) {
    const user = useAuthStore(s => s.user);
    const isReviewer = user?.role === 'admin' || user?.role === 'reviewer';
    const [idx, setIdx] = useState(start);
    const [dotsOpen, setDotsOpen] = useState(false);
    const overlayRef = useRef(null);
    const scrollRef = useRef(null);
    const endRef = useRef(null);
    const sent = useRef(new Set(d.read_sections || []));
    const n = d.sections.length;
    const sec = d.sections[idx];
    const readSet = new Set(d.read_sections || []);
    const last = idx >= n - 1;
    // Parties encore à lire (hors partie affichée, hors parties verrouillées de l'aperçu).
    const unread = d.sections.filter(x => !x.locked && x.idx !== idx && !readSet.has(x.idx)).map(x => x.idx);
    // « Terminer » seulement quand toutes les autres parties sont lues : la partie affichée l'est en terminant.
    const allRead = !!d.read || unread.length === 0;
    // « Suivante » mène à la prochaine partie non lue (après, sinon la première avant) ; tout lu : la suivante.
    const target = unread.find(i => i > idx) ?? unread[0] ?? (idx + 1 < n ? idx + 1 : null);
    const targetLabel = target === null ? null
        : (unread.includes(target) && target !== idx + 1) ? `Partie ${target} (non lue)` : null;
    const accent = scopeInfo?.couleur_theme || null;

    const markRead = useCallback((i) => {
        const s = d.sections[i];
        if (!s || s.locked || sent.current.has(i)) return;
        sent.current.add(i);
        api.post('/notions/read.php', { notion_id: d.id, section: i })
            .then(({ data }) => onRead?.(data.data?.read_sections || [...sent.current], !!data.data?.read))
            .catch(() => sent.current.delete(i));
    }, [d, onRead]);

    // Retour en haut au changement de partie SEULEMENT : marquer une partie lue recharge la fiche (d, sec, markRead
    // changent) et ne doit pas faire remonter le lecteur arrivé en bas de la partie.
    useEffect(() => {
        scrollRef.current?.scrollTo?.({ top: 0 });
        overlayRef.current?.scrollTo?.({ top: 0 });
    }, [idx]);
    // Partie lue : sa fin arrive à l'écran (ou « Suivante »).
    const secLocked = !!sec?.locked;
    useEffect(() => {
        if (!endRef.current || secLocked) return undefined;
        const io = new IntersectionObserver((entries) => { if (entries.some(e => e.isIntersecting)) markRead(idx); }, { threshold: 0.5 });
        io.observe(endRef.current);
        return () => io.disconnect();
    }, [idx, secLocked, markRead]);
    useEffect(() => {
        const h = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight' && target !== null) { markRead(idx); setIdx(target); }
            else if (e.key === 'ArrowLeft' && idx > 0) setIdx(idx - 1);
        };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose, idx, target, markRead]);

    const go = (i) => { if (i >= 0 && i < n) setIdx(i); };
    const next = () => { markRead(idx); if (target !== null) go(target); };
    const finish = () => { markRead(idx); onClose(); };

    return (
        <div ref={overlayRef} className={`quiz-modal-overlay notion-reader-overlay${onTop ? ' notion-reader-overlay--top' : ''} ${!onTop && overlayBg.backgroundImage ? 'has-bg-image' : ''}`}
            style={onTop ? undefined : overlayBg} onClick={onTop ? onClose : undefined}>
            {!onTop && <ThemeToggle className="quiz-theme-toggle quiz-theme-toggle--right" />}
            {!onTop && <button className="quiz-modal-close quiz-modal-close--fixed" onClick={onClose} title="Fermer la fiche"><X size={22} strokeWidth={2} /></button>}
            <div className={`quiz-page notion-reader ${isReviewer ? '' : 'no-select'}`} style={accent ? { '--notion-accent': accent } : undefined}
                onClick={onTop ? (e) => e.stopPropagation() : undefined}>
                {/* Ouverte depuis une question : on le dit, et on y revient (la séance reste ouverte derrière). */}
                {onTop && (
                    <div className="notion-reader__topbar">
                        <button type="button" className="btn btn-ghost btn-sm notion-reader__back" onClick={onClose}><ChevronLeft size={16} /> Retour à la question</button>
                        <span className="notion-reader__topbar-label"><BookOpen size={14} /> Fiche · ta séance reste ouverte derrière</span>
                    </div>
                )}
                <div className="quiz-page-scroll" ref={scrollRef}>
                    {scopeInfo && !onTop && (
                        <div className="quiz-scope-banner" style={accent ? { '--scope-accent': accent } : {}}>
                            {scopeInfo.image && (
                                <div className="quiz-scope-banner-hero">
                                    <img src={scopeInfo.image} alt="" className="quiz-scope-banner-img" />
                                    <div className="quiz-scope-banner-gradient" />
                                </div>
                            )}
                            <div className="quiz-scope-banner-content">
                                <div className="quiz-scope-banner-info">
                                    <span className="quiz-scope-banner-title">{scopeInfo.label}</span>
                                    {scopeInfo.description && <span className="quiz-scope-banner-desc">{scopeInfo.description}</span>}
                                </div>
                                <span className="quiz-scope-banner-cat">{d.matiere || 'Notion'}</span>
                            </div>
                        </div>
                    )}

                    <div className="quiz-header">
                        <div className="quiz-header-top">
                            <h2>{d.titre}</h2>
                            <span className="quiz-mode-badge apprentissage">Fiche</span>
                        </div>
                        <div className="quiz-meta">
                            <span className="quiz-timer">Partie {idx} / {n - 1}</span>
                            <button type="button" className="quiz-dots-toggle" onClick={() => setDotsOpen(o => !o)} aria-expanded={dotsOpen}>
                                Parties {dotsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <ProgressBar current={idx + 1} total={n} />
                        </div>
                    </div>

                    <div className={`quiz-mini-nav${dotsOpen ? ' is-open' : ''}`}>
                        <div className="mini-nav-dots">
                            {d.sections.map(s => (
                                <button key={s.idx} type="button" title={s.titre}
                                    className={`dot ${s.idx === idx ? 'active' : ''} ${readSet.has(s.idx) ? 'correct' : ''} ${s.locked ? 'notion-dot-locked' : ''}`}
                                    onClick={() => go(s.idx)}>
                                    {s.locked ? <Lock size={11} /> : s.idx}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="question-card notion-fiche">
                        <p className="notion-reader__kicker">{idx === 0 ? "L'essentiel" : `Partie ${idx}`}{readSet.has(idx) ? ' · lue' : ''}</p>
                        {sec.locked ? (
                            <>
                                <h2>{sec.titre}</h2>
                                <Unlock d={d} onClaimed={onClaimed} />
                            </>
                        ) : (
                            <>
                                {sec.blocks ? (
                                    <>
                                        {idx > 0 && <h2>{sec.titre}</h2>}
                                        <RichContent blocks={sec.blocks} />
                                    </>
                                ) : <div dangerouslySetInnerHTML={{ __html: sec.html || '' }} />}
                                <div ref={endRef} className="notion-reader__sentinel" />
                            </>
                        )}
                    </div>

                    {!onTop && last && allRead && d.playable && (
                        <div className="notion-reader__done">
                            <CheckCircle size={18} /> <span>Fiche lue en entier : le test est ouvert.</span>
                        </div>
                    )}

                    <div className="quiz-step-nav">
                        <button type="button" className="btn btn-ghost" onClick={() => go(idx - 1)} disabled={idx === 0}><ChevronLeft size={18} /> Précédente</button>
                        {onTop
                            ? <button type="button" className="btn btn-primary" onClick={onClose}>Retour</button>
                            : allRead
                            ? <button type="button" className="btn btn-primary" onClick={finish}>{d.playable ? 'Se tester' : 'Fermer'}</button>
                            : <span className="quiz-step-nav__count">{idx} / {n - 1}</span>}
                        {target !== null
                            ? <button type="button" className="btn btn-ghost" onClick={next}>{targetLabel || 'Suivante'} <ChevronRight size={18} /></button>
                            : <span className="notion-reader__spacer" />}
                    </div>

                    <div className="quiz-nav notion-reader__nav3">
                        <button className="btn btn-ghost btn-sm" onClick={() => go(idx - 1)} disabled={idx === 0}><ChevronLeft size={16} /> Précédente</button>
                        {onTop
                            ? <button className="btn btn-primary" onClick={onClose}>Retour à la question</button>
                            : allRead && <button className="btn btn-primary" onClick={finish}>{d.playable ? 'Terminer et se tester' : 'Fermer'}</button>}
                        {target !== null && (
                            <button className={`btn ${allRead ? 'btn-ghost btn-sm' : 'btn-primary'}`} onClick={next}>{targetLabel || 'Partie suivante'} <ChevronRight size={16} /></button>
                        )}
                    </div>
                </div>
            </div>
            {n > 1 && (
                <>
                    <button className="quiz-nav-arrow quiz-nav-arrow--left" onClick={() => go(idx - 1)} disabled={idx === 0} aria-label="Partie précédente"><ChevronLeft size={28} /></button>
                    <button className="quiz-nav-arrow quiz-nav-arrow--right" onClick={next} disabled={target === null} aria-label="Partie suivante"><ChevronRight size={28} /></button>
                </>
            )}
        </div>
    );
}

function Unlock({ d, onClaimed }) {
    const buy = useContext(BuyCtx);
    const buyable = isBuyable(d);
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [packsOpen, setPacksOpen] = useState(false);
    const [packId, setPackId] = useState(null);
    const canClaim = buyable && buy && buy.credits > 0;
    const packs = buy?.packs || [];
    const pack = packs.find(p => p.id === packId) || packs[0];
    const claim = () => {
        setBusy(true); setErr(null);
        buy.claim(d).then(() => onClaimed?.()).catch(e => setErr(e.response?.data?.message || 'Erreur')).finally(() => setBusy(false));
    };
    if (!buyable || !buy) return <div className="nlib-buy"><button type="button" className="btn nlib-btn" disabled>Achat indisponible</button></div>;

    if (canClaim) {
        return (
            <div className="nlib-buy">
                <div className="nlib-buy__row">
                    <div>
                        <strong className="nlib-buy__claim">Il te reste {buy.credits > 1 ? `${buy.credits} notions` : 'une notion'} à choisir</strong>
                        <span className="nlib-buy__sub">Avec ton abonnement ou ton pack : la fiche complète et ses {d.nb_questions} questions.</span>
                    </div>
                    {!confirm && <button type="button" className="btn nlib-btn" onClick={() => setConfirm(true)}>Choisir cette notion</button>}
                </div>
                {confirm && (
                    <div className="nlib-buy__confirm">
                        <span>« {d.titre} » sera débloquée avec l'une de tes notions à choisir. Ce choix est définitif.</span>
                        <div>
                            <button type="button" className="btn nlib-btn" onClick={claim} disabled={busy}>{busy ? 'Un instant…' : 'Confirmer'}</button>
                            <button type="button" className="btn btn-ghost" onClick={() => setConfirm(false)} disabled={busy}>Annuler</button>
                        </div>
                    </div>
                )}
                {err && <span className="notion-unlock__err">{err}</span>}
                <div className="nlib-buy__note">
                    <span>Tu préfères garder tes notions à choisir ?</span>
                    <button type="button" className="nlib-link" onClick={() => buy.buyNow({ id: d.id, titre: d.titre, prix: d.prix })}>Acheter celle-ci · {fmtPrix(d.prix)}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="nlib-buy">
            <div className="nlib-buy__row">
                <div>
                    <strong className="nlib-buy__price">{fmtPrix(d.prix)}</strong>
                    <span className="nlib-buy__sub">Fiche complète et {d.nb_questions} questions · accès illimité</span>
                </div>
                <button type="button" className="btn nlib-btn" onClick={() => buy.buyNow({ id: d.id, titre: d.titre, prix: d.prix })}>Acheter · {fmtPrix(d.prix)}</button>
            </div>
            {packs.length > 0 && (
                <div className="nlib-buy__note">
                    <span>Plusieurs sujets à réviser ?</span>
                    <button type="button" className="nlib-link" aria-expanded={packsOpen} onClick={() => setPacksOpen(v => !v)}>
                        {packs.map(p => `${p.notions} notions · ${fmtEuro(p.price)}`).join(' / ')}
                    </button>
                </div>
            )}
            {packsOpen && pack && (
                <div className="nlib-packs">
                    <p>Choisis tes notions de cette thématique une à une, maintenant ou plus tard, quel que soit leur prix à l'unité.</p>
                    {packs.map(p => (
                        <label key={p.id} className={`nlib-pack${pack.id === p.id ? ' is-on' : ''}`}>
                            <input type="radio" name={`pack-${d.id}`} checked={pack.id === p.id} onChange={() => setPackId(p.id)} />
                            <span className="nlib-pack__txt"><strong>{p.notions} notions au choix</strong><span>{fmtEuro(Math.round(p.price / p.notions * 100) / 100)} par notion</span></span>
                            <strong className="nlib-pack__price">{fmtEuro(p.price)}</strong>
                        </label>
                    ))}
                    <button type="button" className="btn nlib-btn nlib-btn--block" onClick={() => buy.buyPack(pack)}>Acheter ce pack · {fmtEuro(pack.price)}</button>
                </div>
            )}
            <p className="nlib-buy__legal">Paiement unique, sans abonnement.</p>
        </div>
    );
}
